import * as THREE from 'three';
import { MORPH, ROOM, WALL } from '../config';
import { buildWallShader } from './wallShaders';
import { WallSurface } from './WallSurface';

const { w, h, d } = ROOM;
const HALF_PI = Math.PI / 2;

/**
 * 6면 배치. 각 평면은 +Z를 기본 법선으로 가지므로, 위치/회전으로 법선이 방 안쪽
 * (카메라)을 향하게 한다. seed는 면마다 노이즈 패턴을 다르게 한다.
 */
const WALL_LAYOUT: ReadonlyArray<{
  width: number;
  height: number;
  position: readonly number[];
  rotation: readonly number[];
  seed: readonly number[];
}> = [
  { width: w, height: h, position: [0, h / 2, -d / 2], rotation: [0, 0, 0], seed: [0, 0] }, // front −Z
  { width: w, height: h, position: [0, h / 2, d / 2], rotation: [0, Math.PI, 0], seed: [11, 3] }, // back +Z
  { width: d, height: h, position: [-w / 2, h / 2, 0], rotation: [0, HALF_PI, 0], seed: [5, 19] }, // left −X
  { width: d, height: h, position: [w / 2, h / 2, 0], rotation: [0, -HALF_PI, 0], seed: [23, 7] }, // right +X
  { width: w, height: d, position: [0, 0, 0], rotation: [-HALF_PI, 0, 0], seed: [31, 13] }, // floor −Y
  { width: w, height: d, position: [0, h, 0], rotation: [HALF_PI, 0, 0], seed: [17, 29] }, // ceiling +Y
];

/**
 * 빛의 방. 6개의 독립 평면(WallSurface)으로 이뤄져 면별로 명화·morph·페이드를
 * 제어할 수 있다 (로드맵의 "벽 콘텐츠 추상화 레이어"). 챕터엔 전 면이 같은 명화를,
 * 피날레엔 면마다 다른 명화를 띄운다.
 */
export class Room {
  readonly group = new THREE.Group();
  readonly walls: WallSurface[];

  constructor() {
    const shader = buildWallShader({
      palette: WALL.palette,
      timeScale: WALL.timeScale,
      morphSoftness: MORPH.softness,
      uvRepeat: MORPH.uvRepeat,
    });

    this.walls = WALL_LAYOUT.map((layout) => {
      const wall = new WallSurface({ ...layout, ...shader });
      this.group.add(wall.mesh);
      return wall;
    });
  }

  get wallCount(): number {
    return this.walls.length;
  }

  setMorphAll(value: number): void {
    for (const wall of this.walls) wall.setMorph(value);
  }

  setFadeAll(value: number): void {
    for (const wall of this.walls) wall.setFade(value);
  }

  /** 전 면에 같은 명화 (챕터). */
  setPaintingAll(texture: THREE.Texture, loaded: boolean): void {
    for (const wall of this.walls) wall.setPainting(texture, loaded);
  }

  /** 특정 면에만 명화 (피날레). */
  setPaintingAt(index: number, texture: THREE.Texture, loaded: boolean): void {
    this.walls[index]?.setPainting(texture, loaded);
  }

  update(dt: number): void {
    for (const wall of this.walls) wall.update(dt);
  }
}
